import React, { useState, useEffect } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Typography,
  CircularProgress,
  MenuItem,
  Select,
  FormControl,
  styled,
  tableCellClasses,
  Box,
  useMediaQuery,
} from "@mui/material";
import { getUrl } from "src/Utils/Common";
import FacebookCircularProgress from "src/Components/shared/shared/Loading";
import { theme } from "../../../Theme/index";
import { useSelector } from "react-redux";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import CustomSelectBox from 'src/Components/shared/shared/CustomSelectBox';
import useRefreshData from "src/Utils/CustomHooks/useRefreshData";

const StyledTableCell = styled(TableCell)(({ theme }) => ({
  [`&.${tableCellClasses.head}`]: {
    padding: "4px", // Further reduce padding
    color: "#4A4A4A",
    fontSize: "10px", // Make text smaller on mobile
    fontWeight: "bold",
    textAlign: "center",
    backgroundColor: "#f9f9f9",
    whiteSpace: "nowrap", // Prevents header wrapping issues
    [theme.breakpoints.between("sm", 1300)]: {
      padding: "2px 2px",
      whiteSpace: "normal",
      wordBreak: "break-word",
      overflowWrap: "break-word",
      lineHeight: 1.2,
      maxWidth: "15px",
      width: "auto",
    },
    [theme.breakpoints.between("sm", 1010)]: {
      padding: "2px 1px",
      whiteSpace: "normal",
      wordBreak: "break-word",
      overflowWrap: "break-word",
      lineHeight: 1.2,
      maxWidth: "12px",
      width: "auto",
    },
  },
  [`&.${tableCellClasses.body}`]: {
    fontSize: "10px",
    textAlign: "center",
    padding: "3px", // Adjust for mobile
    borderBottom: "none",
    [theme.breakpoints.between("sm", 1300)]: {
      padding: "2px 2px",
    },
    [theme.breakpoints.between("sm", 1010)]: {
      padding: "2px 1px",
      maxWidth: "12px",
      width: "auto",
    },
  },
}));

const StyledTableRow = styled(TableRow)(() => ({
  height: 35,
}));

const Badge = styled(Box)(({ theme, value }) => ({
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  padding: "4px 40px",
  borderRadius: "12px",
  fontSize: "10px",
  fontWeight: "bold",
  backgroundColor: "#FFFFFF", // Always white background
  border: `2px solid ${value < 0 ? "#CD393B" : "#08306B"}`, // Red border if negative, Blue if positive
  color: value < 0 ? "#CD393B" : "#08306B", // Red text if negative, Blue if positive
  minWidth: "28px",
  maxWidth: "50px",
  textAlign: "center",
  flexWrap: "wrap",
  wordBreak: "break-word",
  overflow: "hidden",
  [theme.breakpoints.between("sm", 1300)]: {
    padding: "2px 20px",
  },
  [theme.breakpoints.between("sm", 1010)]: {
    padding: "2px 5px",
    maxWidth: "none",
    minWidth: "15px",
    width: "auto",
    whiteSpace: "normal",
    wordBreak: "normal",
    overflowWrap: "normal",
    lineHeight: 1.2,
  },
}));

function MarketComparisonTable({ 
  comparisonType, 
  setComparisonType,
  ...props 
}) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // Use props if provided, otherwise use local state
  const [localComparisonType, setLocalComparisonType] = useState("P12M");
  const finalComparisonType = comparisonType !== undefined ? comparisonType : localComparisonType;
  const finalSetComparisonType = setComparisonType || setLocalComparisonType;
  
  const projectId = useSelector((state) => state.userProject.projectId);
  const selectedFilter = useSelector((state) => state.filter.selectedFilter); // ✅ Get Redux filter
  const isMobile = useMediaQuery("(max-width: 600px)"); // Detects mobile screens
  const currentChartType = useSelector((state) => state.chart.currentChartType);

  const resetTableData = () => {
    setData(null);
  };

  const resetTableFilters = () => {
    if (setComparisonType) {
      setComparisonType("P12M");
    } else {
      setLocalComparisonType("P12M");
    }
  };

  const resetTableError = () => {
    setError(null);
  };

  const resetTableLoading = () => {
    setLoading(false);
  };

  const { handleRefresh, isRefreshing } = useRefreshData(
    () => {
      setLoading(true);
    },
    [resetTableData, resetTableFilters, resetTableError, resetTableLoading]
  );

  const getApiFilters = () => {
    if (!selectedFilter || selectedFilter.length === 0) return {};

    return selectedFilter.reduce((acc, item) => {
      const { category, name } = item;

      if (!acc[category]) {
        acc[category] = []; // Initialize category array if not exists
      }

      acc[category].push(name); // Push the name value
      return acc;
    }, {});
  };
  useEffect(() => {
    function fetchMarketComparison() {
      setLoading(true);
      setError(null);

      if (!projectId) {
        console.warn("Project ID is not set in Redux state.");
        setError("Project ID not available");
        setLoading(false);
        return;
      }
      const [hierarchy, hierarchy_value] = currentChartType?.split(",") || [
        "brand",
        "BRAND1",
      ];
      const filters = getApiFilters();

      fetch(
        `${getUrl()}/market_overview/market_comparison`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            "x-cors-api-key": "temp_8c8f91f122df0ef9daa49a6701f13f76",
          },
          body: JSON.stringify({
            project_id: projectId,
            manufacturer: "",
            time_period: finalComparisonType,
            filters: filters,
            is_manufacturer: true,
            target: 3,
          }),
        }
      )
      .then((response) => {
        if (!response.ok) {
          throw new Error(`HTTP error! Status: ${response.status}`);
        }
        return response.json();
      })
      .then((result) => {
        console.log(result);
        
        if (!result || Object.keys(result).length === 0) {
          throw new Error("No data available for selected filters");
        }
        
        const hasValueSales = result.Value_Sales && (result.Value_Sales.Marketplace || result.Value_Sales.Manufacturer);
        if (!hasValueSales) {
          throw new Error("Invalid data structure received from API");
        }
        
        setData(result);
        setError(null);
      }) 
      .catch ((error) => {
        console.error("Error fetching market comparison data:", error);
        setError(error.message || "Failed to load data");
      }) 
      .finally (() => {
        setLoading(false);
      });
    }

    fetchMarketComparison();
  }, [finalComparisonType, currentChartType, selectedFilter]);

  const formatValueSales = (value) => {
    if (typeof value === "number") {
      const inMillions = value / 1_000_000;
      return `${inMillions.toFixed(2)}`;
    }
    return value;
  };
  
  return (
    <Box sx={{ p: 1, display: "flex", flexDirection: "column" }}>
      <Box
        sx={(theme) => ({
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          px: 1,
          mb: 2,
          position: "relative",
          minWidth: { sm: 550 },
          gap: { xs: 6 },
          [theme.breakpoints.between("sm", 1300)]: {
            minWidth: "100%",
          },
        })}
      >
        <Typography
          sx={{
            fontWeight: "bold",
            fontSize: {
              xs: "0.9rem",
              sm: "1rem",
              md: "1.1rem",
            },
            px: {
              xs: 0.5,
              sm: 0.75,
              md: 1,
            },
            color: theme.palette.primary.main,
            textAlign: "left",
            m: 0,
            lineHeight: 1,
            display: "flex",
            alignItems: "center",
            height: 48,
          }}
        >
          Market Comparison
        </Typography>
        <CustomSelectBox
          value={finalComparisonType}
          onChange={(e) => finalSetComparisonType(e.target.value)}
          options={[
            { value: "years", label: "Last Year" },
            { value: "months", label: "Current Month vs Last Month" },
            { value: "months_SameYear", label: "Last Year (Same Month)" },
            { value: "quarters", label: "Current Quarter" },
            { value: "P12M", label: "MAT24" },
          ]}
          renderValue={() => {
            const selectedOption = [
              { value: "years", label: "Last Year" },
              { value: "months", label: "Current Month vs Last Month" },
              { value: "months_SameYear", label: "Last Year (Same Month)" },
              { value: "quarters", label: "Current Quarter" },
              { value: "P12M", label: "MAT24" },
            ].find((opt) => opt.value === finalComparisonType);
            return selectedOption ? selectedOption.label : finalComparisonType;
          }}
        />
      </Box>

      {loading || isRefreshing ? (
        <FacebookCircularProgress />
      ) : error ? (
        <Box
          sx={{
            width: "100%",
            minHeight: 400,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            margin: "0 auto",
          }}
        >
          <FacebookCircularProgress
            loading={false}
            hasData={false}
            onRefresh={handleRefresh}
            message={`${error} - Click to reset table`}
          />
        </Box>
      ) : !data ? (
        <Box
          sx={{
            width: "100%",
            minHeight: 400,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            margin: "0 auto",
          }}
        >
          <FacebookCircularProgress
            loading={false}
            hasData={false}
            onRefresh={handleRefresh}
            message="No data available - Click to reset table"
          />
        </Box>
      ) : (
        <>
          {/* Make Table Responsive */}
          <Box
            sx={(theme) => ({
              width: "100%",
              maxWidth: "100vw",
              overflowX: "auto",
              display: "block",
              padding: "10px",
              [theme.breakpoints.between("sm", 1300)]: {
                padding: "5px",
              },
              [theme.breakpoints.between("sm", 1010)]: {
                padding: "2px 1px",
                overflowX: "auto",
                "&::-webkit-scrollbar": {
                  height: "6px",
                },
                "&::-webkit-scrollbar-thumb": {
                  backgroundColor: "#ccc",
                  borderRadius: "3px",
                },
              },
            })}
          >
            <TableContainer
              component={Paper}
              sx={(theme) => ({
                borderRadius: "10px",
                width: "100%",
                overflowX: "hidden",
                maxWidth: "100%",
                display: "block",
                whiteSpace: "nowrap",
                "&::-webkit-scrollbar": { display: "none" },
                [theme.breakpoints.between("sm", 1300)]: {
                  width: "100%",
                  maxWidth: "100%",
                },
                [theme.breakpoints.between("sm", 1010)]: {
                  overflowX: "auto",
                  width: "100%",
                  maxWidth: "100%",
                  "&::-webkit-scrollbar": {
                    height: "6px",
                  },
                  "&::-webkit-scrollbar-thumb": {
                    backgroundColor: "#ccc",
                    borderRadius: "3px",
                  },
                },
              })}
            >
              <Table 
                stickyHeader 
                aria-label="market comparison table"
                sx={(theme) => ({
                  [theme.breakpoints.between("sm", 1300)]: {
                    width: "100%",
                    tableLayout: "auto",
                  },
                  [theme.breakpoints.between("sm", 1010)]: {
                    width: "max-content",
                    minWidth: "100%",
                    tableLayout: "auto",
                  },
                })}
              >
                {isMobile ? (
                  <TableHead>
                    <TableRow>
                      <StyledTableCell>
                        {isMobile ? "Metric" : "Segments"}
                      </StyledTableCell>
                      <StyledTableCell
                        sx={{ textAlign: "center", color: "#6C63FF" }}
                      >
                        Market
                      </StyledTableCell>
                      <StyledTableCell
                        sx={{ textAlign: "center", color: "#1abc9c" }}
                      >
                        {localStorage.getItem("name")}
                      </StyledTableCell>
                    </TableRow>
                  </TableHead>
                ) : (
                  <TableHead>
                    <TableRow>
                      <StyledTableCell></StyledTableCell>
                      <StyledTableCell>Value Sales (M)</StyledTableCell>
                      <StyledTableCell>Growth (%)</StyledTableCell>
                      <StyledTableCell>Volume Sales (M)</StyledTableCell>
                      <StyledTableCell>Growth (%)</StyledTableCell>
                      <StyledTableCell>Price Index</StyledTableCell>
                    </TableRow>
                  </TableHead>
                )}
                <TableBody>
                  {isMobile ? (
                    // Mobile Layout - Row Headers
                    <>
                      {[
                        { label: "Value Sales ($)", key: "Value_Sales" },
                        { label: "Growth (%)", key: "Value_Sales_Growth" },
                        { label: "Volume Sales", key: "Volume_Sales" },
                        { label: "Growth (%)", key: "Volume_Sales_Growth" },
                        { label: "Price Index", key: "Price_Index" },
                      ].map((row, index) => {
                        const marketValue = data?.[row.key]?.Marketplace || 0;
                        const manufacturerValue =
                          data?.[row.key]?.Manufacturer || 0;

                        return (
                          <StyledTableRow key={index}>
                            <StyledTableCell
                              sx={{
                                fontWeight: "bold",
                                textAlign: "left",
                                backgroundColor: "#f9f9f9",
                              }}
                            >
                              {row.label}
                            </StyledTableCell>
                            <StyledTableCell>
                              <Badge value={marketValue}>
                                {marketValue.toFixed(2)}
                              </Badge>
                            </StyledTableCell>
                            <StyledTableCell>
                              <Badge value={manufacturerValue}>
                                {manufacturerValue.toFixed(2)}
                              </Badge>
                            </StyledTableCell>
                          </StyledTableRow>
                        );
                      })}
                    </>
                  ) : (
                    // Desktop Layout - Column Headers
                    <>
                      {["Marketplace", "Manufacturer"].map((type, index) => (
                        <StyledTableRow key={index}>
                          <StyledTableCell
                            sx={(theme) => ({
                              fontWeight: "bold",
                              color: "#08306B",
                              [theme.breakpoints.between("sm", 1010)]: {
                                whiteSpace: "normal",
                                wordBreak: "break-word",
                                overflowWrap: "break-word",
                                maxWidth: "100px",
                                width: "auto",
                                lineHeight: 1.2,
                              },
                            })}
                          >
                            {type === "Manufacturer"
                              ? localStorage.getItem("name")
                              : type}
                          </StyledTableCell>
                          {[
                            "Value_Sales",
                            "Value_Sales_Growth",
                            "Volume_Sales",
                            "Volume_Sales_Growth",
                            "Price_Index_Growth",
                          ].map((key) => {
                            const value = data?.[key]?.[type] || 0;
                            const formattedValue = [
                              "Value_Sales",
                              "Volume_Sales",
                            ].includes(key)
                              ? formatValueSales(value)
                              : value.toFixed(2);
                            return (
                              <StyledTableCell key={key}>
                                <Badge value={value}>{formattedValue}</Badge>
                              </StyledTableCell>
                            );
                          })}
                        </StyledTableRow>
                      ))}
                    </>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </Box>
        </>
      )}
    </Box>
  );
}

export default MarketComparisonTable;